import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "../utils/authToken";
import { useAuth } from "../context/AuthContext";
import {
  Package, ChevronRight, ShoppingBag,
  CheckCircle, Truck, Home, Clock, XCircle, BookOpen,
} from "lucide-react";

const STATUS = {
  confirmed:  { label: "Confirmed",  color: "#3B82F6", bg: "rgba(59,130,246,0.12)",  icon: CheckCircle },
  processing: { label: "Processing", color: "#F59E0B", bg: "rgba(245,158,11,0.12)",  icon: Clock       },
  shipped:    { label: "Shipped",    color: "#8B5CF6", bg: "rgba(139,92,246,0.12)",  icon: Truck       },
  delivered:  { label: "Delivered",  color: "#10B981", bg: "rgba(16,185,129,0.12)",  icon: Home        },
  cancelled:  { label: "Cancelled",  color: "#EF4444", bg: "rgba(239,68,68,0.12)",   icon: XCircle     },
};

const STEPS = ["confirmed", "processing", "shipped", "delivered"];

const BOOK_GRADIENTS = [
  ["#1A1108","#C8941F"], ["#0A1E14","#1A5228"], ["#130F1E","#3D1A70"],
  ["#1E0804","#6B2018"], ["#080F1E","#1A2A6B"], ["#1A0A0A","#5A1818"],
];

function Skeleton() {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
      {[1,2,3].map(i => (
        <div key={i} style={{ background:"var(--card-bg)", border:"1px solid var(--card-border)", borderRadius:"var(--radius-lg)", padding:"1.25rem", display:"flex", gap:"1rem" }}>
          <div style={{ width:52, height:72, borderRadius:8, background:"var(--dust)", animation:"sk 1.4s ease infinite", flexShrink:0 }} />
          <div style={{ flex:1, display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ width:"60%", height:14, borderRadius:4, background:"var(--dust)", animation:"sk 1.4s ease infinite" }} />
            <div style={{ width:"40%", height:11, borderRadius:4, background:"var(--dust)", animation:"sk 1.4s ease infinite" }} />
            <div style={{ width:"80%", height:4, borderRadius:99, background:"var(--dust)", animation:"sk 1.4s ease infinite", marginTop:4 }} />
          </div>
        </div>
      ))}
      <style>{`@keyframes sk{0%,100%{opacity:1}50%{opacity:.35}}`}</style>
    </div>
  );
}

function OrderCard({ order, onClick }) {
  const [hovered, setHovered] = useState(false);
  const meta = STATUS[order.status] || STATUS.confirmed;
  const stepIdx = STEPS.indexOf(order.status);
  const Icon = meta.icon;
  const progress = stepIdx === -1 ? 0 : ((stepIdx + 1) / STEPS.length) * 100;
  const items = order.items || [];
  const firstItem = items[0];
  const grad = BOOK_GRADIENTS[Math.abs((order.id?.charCodeAt(0) || 0) % BOOK_GRADIENTS.length)];

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "var(--card-bg)",
        border: `1px solid ${hovered ? meta.color : "var(--card-border)"}`,
        borderRadius: "var(--radius-lg)",
        overflow: "hidden", cursor: "pointer",
        transition: "all 0.22s ease",
        boxShadow: hovered ? `0 8px 24px ${meta.color}22` : "var(--shadow-xs)",
        transform: hovered ? "translateY(-3px)" : "none",
      }}
    >
      {/* Status color bar */}
      <div style={{ height:"3px", background:`linear-gradient(90deg, ${meta.color} ${progress}%, var(--dust-2) ${progress}%)` }} />

      <div style={{ padding:"1rem 1.1rem" }}>
        {/* Top row */}
        <div style={{ display:"flex", alignItems:"flex-start", gap:"12px", marginBottom:"1rem" }}>

          {/* Book cover stack */}
          <div style={{ position:"relative", flexShrink:0, width:"52px", height:"70px" }}>
            {items.slice(0,2).reverse().map((_, i) => (
              <div key={i} style={{
                position: i === 0 && items.length > 1 ? "absolute" : "relative",
                top: i === 0 ? 4 : 0, left: i === 0 ? 4 : 0,
                width:"46px", height:"64px", borderRadius:"5px",
                background:`linear-gradient(155deg, ${grad[0]}, ${grad[1]})`,
                display:"flex", alignItems:"center", justifyContent:"center",
                boxShadow: i === 1 ? "2px 2px 8px rgba(0,0,0,0.35)" : "none",
                border: i === 0 ? "1px solid rgba(255,255,255,0.06)" : "none",
                zIndex: i === 1 ? 1 : 0,
              }}>
                <div style={{ position:"absolute", left:0, top:0, bottom:0, width:"4px", background:"rgba(0,0,0,0.3)", borderRadius:"5px 0 0 5px" }} />
                {i === 1 && <BookOpen size={14} color="rgba(255,255,255,0.5)" />}
              </div>
            ))}
            {items.length > 2 && (
              <div style={{
                position:"absolute", bottom:-4, right:-4,
                background: meta.color, color:"#fff",
                fontSize:"0.6rem", fontWeight:700,
                width:18, height:18, borderRadius:"50%",
                display:"flex", alignItems:"center", justifyContent:"center",
                border:"2px solid var(--card-bg)", zIndex:2,
              }}>+{items.length - 2}</div>
            )}
          </div>

          {/* Info */}
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:"3px" }}>
              <span style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"1rem", color:"var(--ink)", letterSpacing:"0.02em" }}>
                #{order.id.slice(-8).toUpperCase()}
              </span>
              {/* Status chip */}
              <div style={{
                display:"inline-flex", alignItems:"center", gap:"5px",
                padding:"3px 10px", borderRadius:"var(--radius-full)",
                background: meta.bg, flexShrink:0,
              }}>
                <Icon size={10} color={meta.color} strokeWidth={2.5} />
                <span style={{ fontSize:"0.68rem", fontWeight:700, color: meta.color, textTransform:"uppercase", letterSpacing:"0.06em" }}>
                  {meta.label}
                </span>
              </div>
            </div>

            <div style={{ fontSize:"0.75rem", color:"var(--muted)", marginBottom:"6px" }}>
              {new Date(order.created_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
              {" · "}
              {items.length} item{items.length !== 1 ? "s" : ""}
            </div>

            {/* First item title */}
            {firstItem && (
              <div style={{ fontSize:"0.82rem", color:"var(--ink)", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {firstItem.title}{items.length > 1 ? ` & ${items.length - 1} more` : ""}
              </div>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {order.status !== "cancelled" && (
          <div style={{ marginBottom:"1rem" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"5px" }}>
              {STEPS.map((step, i) => (
                <span key={step} style={{
                  fontSize:"0.62rem", fontWeight: i <= stepIdx ? 600 : 400,
                  color: i <= stepIdx ? meta.color : "var(--muted-2)",
                  textTransform:"uppercase", letterSpacing:"0.04em",
                }}>
                  {step.charAt(0).toUpperCase() + step.slice(1)}
                </span>
              ))}
            </div>
            <div style={{ height:"4px", borderRadius:"99px", background:"var(--dust-2)", overflow:"hidden" }}>
              <div style={{
                height:"100%", width:`${progress}%`,
                background:`linear-gradient(90deg, ${meta.color}99, ${meta.color})`,
                borderRadius:"99px", transition:"width 0.4s ease",
              }} />
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:"0.65rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:"1px" }}>Total Paid</div>
            <div style={{ fontFamily:"var(--font-display)", fontSize:"1.25rem", fontWeight:800, color:"var(--ink)" }}>
              ₹{(order.total / 100).toFixed(0)}
            </div>
          </div>
          <div style={{
            display:"flex", alignItems:"center", gap:"6px",
            padding:"8px 18px",
            background: hovered ? meta.color : "transparent",
            color: hovered ? "#fff" : meta.color,
            border:`1.5px solid ${meta.color}`,
            borderRadius:"var(--radius-full)",
            fontSize:"0.8rem", fontWeight:700,
            transition:"all 0.2s",
          }}>
            View Order <ChevronRight size={13} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MyOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) { navigate("/login"); return; }
    if (user) fetchOrders();
  }, [user, navigate]);

  async function fetchOrders() {
    try {
      const token = await getAuthToken();
      const res = await fetch("/api/orders/list", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch orders", err);
    } finally {
      setLoading(false);
    }
  }

  const totalSpent = orders.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="page-enter" style={{ minHeight:"calc(100vh - var(--nav-h))", background:"var(--cream)" }}>

      {/* Hero header */}
      <div style={{
        background:"linear-gradient(135deg, var(--ink) 0%, #1a2744 100%)",
        padding:"2rem 1.25rem 3.5rem",
        position:"relative", overflow:"hidden",
      }}>
        <div style={{ position:"absolute", top:-40, right:-40, width:200, height:200, borderRadius:"50%", background:"rgba(255,255,255,0.03)" }} />
        <div style={{ position:"absolute", bottom:-60, left:-20, width:160, height:160, borderRadius:"50%", background:"rgba(255,255,255,0.03)" }} />
        <div style={{ maxWidth:"680px", margin:"0 auto", position:"relative", zIndex:1 }}>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:"1rem" }}>
            <div>
              <h1 style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:"clamp(1.8rem,5vw,2.4rem)", color:"#fff", margin:"0 0 6px" }}>
                My Orders
              </h1>
              {!loading && (
                <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"0.85rem", margin:0 }}>
                  {orders.length} order{orders.length !== 1 ? "s" : ""} · ₹{(totalSpent / 100).toFixed(0)} total spent
                </p>
              )}
            </div>
            <button onClick={() => navigate("/store")} style={{
              display:"flex", alignItems:"center", gap:"6px",
              padding:"9px 20px",
              background:"rgba(255,255,255,0.1)",
              color:"#fff", border:"1px solid rgba(255,255,255,0.2)",
              borderRadius:"var(--radius-full)",
              fontFamily:"var(--font-body)", fontWeight:600, fontSize:"0.82rem",
              cursor:"pointer", backdropFilter:"blur(8px)",
              transition:"background 0.2s",
            }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
              onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
            >
              <ShoppingBag size={13} /> Shop More
            </button>
          </div>
        </div>
      </div>

      {/* Cards pulled up over hero */}
      <div style={{ maxWidth:"680px", margin:"-1.5rem auto 0", padding:"0 1.25rem 6rem", position:"relative", zIndex:2 }}>

        {loading && <Skeleton />}

        {!loading && orders.length === 0 && (
          <div style={{
            textAlign:"center", padding:"4rem 2rem",
            background:"var(--card-bg)", border:"1px solid var(--card-border)",
            borderRadius:"var(--radius-lg)", boxShadow:"var(--shadow-md)",
          }}>
            <div style={{ width:72, height:72, borderRadius:"50%", background:"var(--gold-faint)", margin:"0 auto 1.25rem", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <ShoppingBag size={30} color="var(--gold)" strokeWidth={1.5} />
            </div>
            <h2 style={{ fontFamily:"var(--font-display)", color:"var(--ink)", margin:"0 0 0.5rem", fontSize:"1.4rem" }}>No Orders Yet</h2>
            <p style={{ color:"var(--muted)", fontSize:"0.88rem", maxWidth:"220px", margin:"0 auto 1.5rem", lineHeight:1.65 }}>
              Your orders will appear here once you make a purchase.
            </p>
            <button onClick={() => navigate("/store")} style={{
              background:"var(--gold)", color:"#fff", border:"none",
              borderRadius:"var(--radius-full)", padding:"11px 28px",
              fontFamily:"var(--font-body)", fontSize:"0.9rem", fontWeight:700,
              cursor:"pointer", boxShadow:"var(--shadow-gold)",
            }}>Browse Store</button>
          </div>
        )}

        {!loading && orders.length > 0 && (
          <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
            {orders.map(order => (
              <OrderCard key={order.id} order={order} onClick={() => navigate(`/orders/${order.id}`)} />
            ))}
          </div>
        )}
      </div>
      <style>{`
        @keyframes pageEnter {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .page-enter { animation: pageEnter 0.32s cubic-bezier(0.22, 1, 0.36, 1) both; }
      `}</style>
    </div>
  );
}
